import React, { useState, useEffect } from 'react';
import { COLORS } from '../App';
import SourcePDFViewer from './SourcePDFViewer';
import CommitteeTablesResult from './CommitteeTablesResult';
import QuickActionResult from './QuickActionResult';
import AppraisalReviewModal from './AppraisalReviewModal';

const API_BASE = window.location.port === '3000' ? 'http://localhost:4100/api' : window.location.pathname.startsWith('/cmbs') ? '/cmbs/api' : '/api';

function fmtBytes(b) {
  if (!b) return '';
  if (b < 1024) return b + ' B';
  if (b < 1048576) return (b / 1024).toFixed(1) + ' KB';
  return (b / 1048576).toFixed(1) + ' MB';
}

function fmtDate(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const STATUS_COLORS = {
  pending: { bg: '#F3F4F6', text: '#6B7280' },
  processing: { bg: '#FEF9C3', text: '#854D0E' },
  complete: { bg: '#DCFCE7', text: '#166534' },
  not_supported: { bg: '#F3F4F6', text: '#6B7280' },
  failed: { bg: '#FEE2E2', text: '#991B1B' },
};

const FLAG_COLORS = {
  info: { bg: '#EFF6FF', border: '#BFDBFE', text: '#1D4ED8', icon: 'ℹ️' },
  warning: { bg: '#FFFBEB', border: '#FDE68A', text: '#92400E', icon: '⚠️' },
  critical: { bg: '#FEF2F2', border: '#FECACA', text: '#991B1B', icon: '🚨' },
};

export default function DocumentActionPanel({ doc, deal, token, onRefresh, isAppraisal }) {
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfInitialPage, setPdfInitialPage] = useState(1);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);

  // Extraction state
  const [extracting, setExtracting] = useState(false);
  const [extractLog, setExtractLog] = useState([]);

  // Committee tables state
  const [committeeTablesHtml, setCommitteeTablesHtml] = useState(null);
  const [committeeTablesMeta, setCommitteeTablesMeta] = useState(null);
  const [generatingTables, setGeneratingTables] = useState(false);

  // Quick actions state
  const [quickResult, setQuickResult] = useState(null);
  const [quickAction, setQuickAction] = useState(null);
  const [quickPrompt, setQuickPrompt] = useState('');
  const [runningQuick, setRunningQuick] = useState(false);
  const [askInput, setAskInput] = useState('');

  // Load cached outputs on mount
  useEffect(() => {
    loadOutputs();
  }, [doc.id]);

  const loadOutputs = async () => {
    try {
      const res = await fetch(`${API_BASE}/diligence/outputs/${doc.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const rows = await res.json();
      if (!Array.isArray(rows)) return;

      const tableRow = rows.find(r => r.output_type === 'committee_tables');
      if (tableRow) {
        setCommitteeTablesHtml(tableRow.content);
        setCommitteeTablesMeta(tableRow.metadata);
      }

      const quickRow = rows.find(r =>
        ['quick_summarize', 'quick_extract_numbers', 'quick_ask'].includes(r.output_type)
      );
      if (quickRow) {
        const actionMap = { quick_summarize: 'summarize', quick_extract_numbers: 'extract_numbers', quick_ask: 'ask' };
        setQuickResult({ action: actionMap[quickRow.output_type], content: quickRow.content, customPrompt: quickRow.metadata?.prompt });
        setQuickAction(actionMap[quickRow.output_type]);
      }
    } catch (err) {
      console.error('Load outputs error:', err);
    }
  };

  const runExtraction = async () => {
    setExtracting(true);
    setExtractLog(['Reading PDF...', 'Analyzing document structure...', 'Extracting property details...']);
    const addLog = (msg) => setExtractLog(prev => [...prev, msg]);
    try {
      addLog('Locating comparable sales table...');
      addLog('Locating comparable rents table...');
      addLog('Generating summary and flags...');
      const res = await fetch(`${API_BASE}/diligence/extract/${doc.id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.json());
      if (res.status === 'complete') addLog('Extraction complete.');
      else if (res.status === 'not_supported') addLog('Not supported: ' + (res.message || ''));
      else if (res.error) addLog('Error: ' + res.error);
    } catch (err) {
      addLog('Error: ' + err.message);
    }
    setExtracting(false);
    onRefresh();
  };

  const generateCommitteeTables = async () => {
    setGeneratingTables(true);
    try {
      const res = await fetch(`${API_BASE}/diligence/committee-tables/${doc.id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.json());
      if (res.html) {
        setCommitteeTablesHtml(res.html);
        setCommitteeTablesMeta({ has_sales: !!res.salesHtml, has_rents: !!res.rentsHtml });
      } else {
        alert(res.error || 'Failed to generate tables');
      }
    } catch (err) {
      alert('Error: ' + err.message);
    }
    setGeneratingTables(false);
  };

  const runQuick = async (action, prompt) => {
    setRunningQuick(true);
    setQuickResult(null);
    setQuickAction(action);
    try {
      const res = await fetch(`${API_BASE}/diligence/quick-action/${doc.id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, customPrompt: prompt }),
      }).then(r => r.json());
      if (res.success) {
        setQuickResult({ action, content: res.content, customPrompt: prompt });
      } else {
        alert(res.error || 'Quick action failed');
        setQuickAction(null);
      }
    } catch (err) {
      alert('Error: ' + err.message);
      setQuickAction(null);
    }
    setRunningQuick(false);
  };

  const extractedData = doc.extracted_data;
  const flags = doc.flags || [];
  const statusStyle = STATUS_COLORS[doc.extraction_status] || STATUS_COLORS.pending;
  const extractionDone = doc.extraction_status === 'complete' && extractedData;

  return (
    <div style={{ background: COLORS.white, borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: 18, marginBottom: 12 }}>
      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <span style={{ fontSize: 20 }}>📑</span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {doc.file_name}
            </div>
            <div style={{ fontSize: 11, color: COLORS.textLight }}>
              {fmtBytes(doc.file_size)} · {fmtDate(doc.created_at)}{doc.total_pages ? ` · ${doc.total_pages} pages` : ''}
            </div>
          </div>
        </div>
        <span style={{
          fontSize: 10, fontWeight: 600, padding: '3px 10px', borderRadius: 10, flexShrink: 0, marginLeft: 8,
          background: statusStyle.bg, color: statusStyle.text,
        }}>
          {doc.extraction_status === 'not_supported' ? 'Coming Soon' : (doc.extraction_status || 'pending')}
        </span>
      </div>

      {/* Always-visible action bar */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <button onClick={() => { setPdfInitialPage(1); setPdfViewerOpen(true); }}
          style={{ padding: '6px 14px', background: COLORS.navy, color: '#fff', border: 'none', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>
          View PDF
        </button>

        {isAppraisal ? (
          <button
            onClick={runExtraction}
            disabled={extracting || doc.extraction_status === 'processing'}
            style={{ padding: '6px 14px', background: COLORS.gold, color: COLORS.navy, border: 'none', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: 'pointer', opacity: extracting ? 0.7 : 1 }}>
            {extracting ? 'Extracting…' : doc.extraction_status === 'complete' ? 'Re-Extract' : 'Run Extraction'}
          </button>
        ) : (
          <button disabled
            style={{ padding: '6px 14px', background: '#F3F4F6', color: '#9CA3AF', border: `1px solid #E5E7EB`, borderRadius: 4, fontSize: 11, cursor: 'not-allowed' }}>
            Extraction Coming Soon
          </button>
        )}

        {/* Primary actions — appraisals only, after extraction */}
        {isAppraisal && extractionDone && (
          <>
            <button onClick={generateCommitteeTables} disabled={generatingTables}
              style={{ padding: '6px 14px', background: '#1D4ED8', color: '#fff', border: 'none', borderRadius: 4, fontSize: 11, cursor: 'pointer', opacity: generatingTables ? 0.6 : 1 }}>
              {generatingTables ? 'Building…' : committeeTablesHtml ? 'Rebuild Tables' : 'Committee Tables'}
            </button>
            <button onClick={() => setReviewModalOpen(true)}
              style={{ padding: '6px 14px', background: '#7C3AED', color: '#fff', border: 'none', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>
              Draft Appraisal Review
            </button>
          </>
        )}
      </div>

      {/* Extraction log while running */}
      {extracting && extractLog.length > 0 && (
        <div style={{ background: '#0F1B2D', borderRadius: 6, padding: '10px 14px', marginBottom: 10 }}>
          {extractLog.map((m, i) => (
            <div key={i} style={{ fontSize: 11, color: '#88BBCC', fontFamily: 'monospace', marginBottom: 2 }}>{m}</div>
          ))}
        </div>
      )}

      {/* Extraction error */}
      {doc.extraction_error && (doc.extraction_status === 'failed' || doc.extraction_status === 'not_supported') && (
        <div style={{
          background: doc.extraction_status === 'failed' ? '#FEE2E2' : '#F3F4F6',
          border: `1px solid ${doc.extraction_status === 'failed' ? '#FECACA' : '#E5E7EB'}`,
          borderRadius: 6, padding: '8px 12px', marginBottom: 10, fontSize: 12,
          color: doc.extraction_status === 'failed' ? '#991B1B' : '#6B7280',
        }}>
          {doc.extraction_error}
        </div>
      )}

      {/* Extraction results */}
      {extractionDone && (
        <div style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: 12, marginTop: 4 }}>
          {doc.summary && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.navy, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Summary</div>
              <p style={{ fontSize: 12, color: COLORS.text, lineHeight: 1.6, margin: 0 }}>{doc.summary}</p>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 12 }}>
            {[
              { label: 'As-Is Value', value: extractedData.concluded_as_is_value ? '$' + Number(extractedData.concluded_as_is_value).toLocaleString() : null },
              { label: 'Cap Rate', value: extractedData.concluded_cap_rate_percent ? extractedData.concluded_cap_rate_percent + '%' : null },
              { label: 'Value PSF', value: extractedData.value_per_sf ? '$' + Number(extractedData.value_per_sf).toLocaleString() : null },
              { label: 'Effective Date', value: extractedData.effective_date_of_value },
              { label: 'Comp Sales', value: extractedData.comp_sales?.length ? extractedData.comp_sales.length + ' comps' : null },
              { label: 'Comp Rents', value: extractedData.comp_rents?.length ? extractedData.comp_rents.length + ' comps' : null },
            ].filter(v => v.value).map((v, i) => (
              <div key={i} style={{ background: '#F8F9FA', borderRadius: 6, padding: '8px 10px', border: `1px solid ${COLORS.border}` }}>
                <div style={{ fontSize: 10, color: COLORS.textLight, marginBottom: 2 }}>{v.label}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.navy }}>{v.value}</div>
              </div>
            ))}
          </div>

          {flags.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.navy, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Flags</div>
              {flags.map((flag, i) => {
                const fc = FLAG_COLORS[flag.level] || FLAG_COLORS.info;
                return (
                  <div key={i} style={{ background: fc.bg, border: `1px solid ${fc.border}`, borderRadius: 6, padding: '7px 12px', marginBottom: 6, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <span style={{ fontSize: 14, flexShrink: 0 }}>{fc.icon}</span>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: 12, color: fc.text }}>{flag.text}</span>
                      {flag.page && (
                        <button onClick={() => { setPdfInitialPage(flag.page); setPdfViewerOpen(true); }}
                          style={{ marginLeft: 8, fontSize: 10, color: fc.text, background: 'transparent', border: `1px solid ${fc.border}`, borderRadius: 3, padding: '1px 6px', cursor: 'pointer' }}>
                          p.{flag.page}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Committee tables result */}
      {committeeTablesHtml && (
        <div style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: 14, marginTop: 4 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.navy, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Committee-Ready Tables</div>
          <CommitteeTablesResult
            html={committeeTablesHtml}
            documentId={doc.id}
            token={token}
            hasCompSales={committeeTablesMeta?.has_sales}
            hasCompRents={committeeTablesMeta?.has_rents}
          />
        </div>
      )}

      {/* Quick actions */}
      <div style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: 12, marginTop: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.navy, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>Quick Actions</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
          {[
            { action: 'summarize', label: 'Summarize in 3 Sentences' },
            { action: 'extract_numbers', label: 'Extract Key Numbers' },
          ].map(({ action, label }) => (
            <button key={action} onClick={() => runQuick(action, undefined)} disabled={runningQuick}
              style={{
                padding: '5px 14px', borderRadius: 20,
                background: quickAction === action && quickResult ? COLORS.navy : '#F0F2F5',
                color: quickAction === action && quickResult ? '#fff' : COLORS.navy,
                border: `1px solid ${COLORS.border}`, fontSize: 11, cursor: 'pointer',
                opacity: runningQuick && quickAction !== action ? 0.5 : 1,
              }}>
              {runningQuick && quickAction === action ? 'Running…' : label}
            </button>
          ))}
        </div>

        {/* Ask Claude form */}
        <form onSubmit={(e) => { e.preventDefault(); if (askInput.trim()) { runQuick('ask', askInput); setAskInput(''); } }}
          style={{ display: 'flex', gap: 6 }}>
          <input
            value={askInput}
            onChange={e => setAskInput(e.target.value)}
            placeholder="Ask Claude anything about this document…"
            disabled={runningQuick}
            style={{
              flex: 1, padding: '6px 12px', border: `1px solid ${COLORS.border}`, borderRadius: 20,
              fontSize: 12, outline: 'none', color: COLORS.text,
            }}
          />
          <button type="submit" disabled={runningQuick || !askInput.trim()}
            style={{ padding: '6px 16px', background: COLORS.gold, color: COLORS.navy, border: 'none', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer', opacity: (!askInput.trim() || runningQuick) ? 0.5 : 1 }}>
            Ask
          </button>
        </form>

        {/* Quick action result */}
        {quickResult && (
          <div style={{ marginTop: 12 }}>
            <QuickActionResult action={quickResult.action} content={quickResult.content} customPrompt={quickResult.customPrompt} />
          </div>
        )}
      </div>

      {/* PDF Viewer */}
      {pdfViewerOpen && (
        <SourcePDFViewer documentId={doc.id} initialPage={pdfInitialPage} onClose={() => setPdfViewerOpen(false)} />
      )}

      {/* Appraisal Review Modal */}
      {reviewModalOpen && (
        <AppraisalReviewModal
          documentId={doc.id}
          documentName={doc.file_name}
          token={token}
          onClose={() => setReviewModalOpen(false)}
        />
      )}
    </div>
  );
}
